import { NavigateBefore, NavigateNext } from '@material-ui/icons'
import { getImage } from 'gatsby-plugin-image'
import React, { createRef, useState } from 'react'
import Slider from 'react-slick'
import styled from 'styled-components'
import AnimateText from '../../helpers/animateText'
import { formatDateTime } from '../../helpers/formatDateTime'
import GetFocalRatio from '../../helpers/getFocalRatio'
import { isCurrentlyOn } from '../../helpers/is-currently-on'
import { navigationVars } from '../../helpers/navigationVars'
import Parallax from '../../helpers/parallax'
import {
  ExtendedH3,
  ExtendedH4,
  ExtendedH6,
  H1,
} from '../Typography/Typography'
import ArrowLink from '../atoms/arrowLink'
import Link from '../atoms/link'
import theme from '../atoms/theme'
import Container from '../container'
import Button from '../molecules/button'
/* Types */

type HeroCarouselNode = {
  node: Queries.ContentfulEvent
}

type HeroCarouselData = {
  readonly items: readonly [HeroCarouselNode]
}

type HeroCarouselProps = {
  items: {
    node: {
      preTitle?: string | null
      title?: string | null
      slug?: string | null
      startDate?: string | null
      endDate?: string | null
      customDateText?: string | null
      eventType?: {
        title?: string | null
        slug?: string | null
      } | null
      categories?:
        | {
            readonly title?: string | null
            readonly slug?: string | null
            readonly id: string
            readonly colour?: string | null
          }[]
        | null
      heroImage?: Queries.ContentfulAsset | null
      imageWithFocalPoint?: {
        focalPoint?: {
          focalPoint?: {
            x: number
            y: number
          } | null
        } | null
        image?: Queries.ContentfulAsset | null
      } | null
      viewMoreButtonText?: string | null
    }
  }[]
}

/* Styles */

const { colours, animation, icon, breakpoints } = theme

const HeroCarouselContainer = styled.div`
  margin-bottom: 13rem;

  @media only screen and ${breakpoints.toNormalScreen} {
    margin-bottom: 2rem;
  }
`

const HeroContainer = styled.div`
  height: 40vh;

  @media only screen and ${breakpoints.fromNormalScreen} {
    height: 81.2rem;
  }
`

const HeroDetails = styled(Container)`
  background-color: ${colours.dark};
  text-align: center;
  position: relative;

  @media only screen and ${breakpoints.toNormalScreen} {
    padding-top: 3.2rem;
    top: -1rem;
    padding-bottom: 11.2rem;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    position: absolute;
    left: 50%;
    text-align: left;
    background-color: transparent;
    top: 0;
    display: flex;
    height: 100%;
    justify-content: center;
    transform: translate(-50%, 0);
    flex-direction: column;
    align-items: flex-start;
    padding-top: ${navigationVars.desktopHeight};
    padding-bottom: 10rem;
  }
`

const HeroControlContainer = styled(Container)`
  position: relative;
  color: ${colours.light};
  z-index: 10;

  @media only screen and ${breakpoints.toNormalScreen} {
    padding: 0;
    display: flex;
    flex-direction: column-reverse;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    margin-top: -13rem;
  }
`

const HeroTitle = styled(H1)`
  color: ${colours.light};
  margin-bottom: 1rem;

  @media only screen and ${breakpoints.fromNormalScreen} {
    width: 50%;
  }
`

const HeroCategory = styled(ExtendedH6)`
  background-color: ${colours.darkOverlay1};
  color: ${colours.light};
  padding: 0.6rem 1rem;

  @media only screen and ${breakpoints.toNormalScreen} {
    position: absolute;
    top: -3.6rem;
    left: 2rem;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    position: relative;
  }
`

const HeroDate = styled(ExtendedH6)`
  color: ${colours.light};
  margin-bottom: 2rem;
  text-transform: none;
`

export const BackgroundFill = styled.div`
  position: relative;
  z-index: 10;
  bottom: 0;

  @media only screen and ${breakpoints.toNormalScreen} {
    background-color: ${colours.dark};
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    &::before {
      content: '';
      position: absolute;
      left: 0;
      right: 50%;
      height: 100%;
      background-color: ${colours.dark};
      z-index: -1;
    }
  }
`

const HeroControlIndicator = styled.div`
  background-color: ${colours.darkTint1};
  position: relative;
  height: 0.5rem;

  @media only screen and ${breakpoints.fromNormalScreen} {
    height: 0.7rem;
    background-color: ${colours.lightOverlay2};
  }

  &::before {
    content: '';
    width: ${(props): string => `${(1 / props.numOfItems) * 100}%`};
    height: 100%;
    position: absolute;
    top: 0;
    left: ${(props): string => `${(1 / props.numOfItems) * 100 * props.index}%`};
    transition: left ${animation.defaultLength} ease-in-out;
    background-color: ${colours.primary};
  }
`

const HeroControls = styled.div`
  display: flex;
  align-items: center;
  background-color: ${colours.dark};
  justify-content: space-between;
  padding: 0.7rem 1rem;

  @media only screen and ${breakpoints.fromNormalScreen} {
    padding: 0.7rem 0.7rem 0.7rem 2.6rem;
    background-color: ${colours.darkOverlay1};
  }

  h3,
  ${ExtendedH3} {
    margin-bottom: 0;

    @media only screen and ${breakpoints.toNormalScreen} {
      display: none;
    }
  }

  .MuiSvgIcon-root {
    width: 3.5rem;
    height: 3.5rem;

    @media only screen and ${breakpoints.fromNormalScreen} {
      width: ${icon.largeSize};
      height: ${icon.largeSize};
    }

    &:hover {
      cursor: pointer;
      color: ${colours.primary};
    }
  }
`

const HeroControlButtons = styled.div`
  display: inline-flex;

  @media only screen and ${breakpoints.toNormalScreen} {
    justify-content: space-between;
    width: 100%;
  }
`

export const MetaInformationContainer = styled(Container)`
  @media only screen and ${breakpoints.toNormalScreen} {
    padding: 0;
  }
`

export const MetaInformation = styled.div`
  background-color: ${colours.dark};
  color: ${colours.light};
  display: flex;
  padding: 2.6rem 0;
  flex-basis: 75%;

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: column;
    padding: 2.6rem 0 0 0;
  }
`

export const MetaSection = styled.div`
  flex-basis: 100%;

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: column;
    border-bottom: 0.1rem solid ${colours.darkTint1};
    padding: 1.8rem 2rem;
    text-align: center;

    &:last-child {
      border-bottom: none;
    }

    h4,
    ${ExtendedH4} {
      margin-bottom: 0;
    }
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    margin-right: 2rem;
  }

  p {
    margin: 0;
  }

  h4,
  ${ExtendedH4} {
    color: ${colours.primary};
  }
`

export const StyledArrowLink = styled(ArrowLink)`
  font-size: 1.8rem;
  display: inline-block;
`

const StyledSlider = styled(Slider)`
  &,
  &:before,
  &:after {
    overflow: hidden;
  }

  .slick-active {
    z-index: 1000;
  }

  @media only screen and ${breakpoints.toNormalScreen} {
    background-color: ${colours.dark};
  }
`

export const CategorySeparator = styled(Link)`
  display: inline-block;
  margin-left: 0.5rem;
  text-decoration: none;
  transition: color ${animation.defaultLength} ease-in-out;

  &::before {
    content: ' - ';
  }

  &:first-child {
    border-left: none;
    margin-left: 0;
    padding-left: 0;

    &::before {
      content: '';
    }
  }
`

// TODO: Make link to categories
export const renderCategories = (
  eventType: { slug: string },
  categories: { slug: string; title: string }[],
  separator: null | string = null,
  as: null | string = null,
): JSX.Element[] => {
  return categories.map((category) => {
    if (separator) {
      return (
        <Link
          className='category'
          as={as}
          to={`/${eventType?.slug}s/${category?.slug}/`}
          key={`render-cats-${eventType?.slug}s/${category?.slug}`}
          tabIndex={-1}
        >
          {category.title} /{' '}
        </Link>
      )
    }

    return (
      <CategorySeparator
        as={as ?? undefined}
        to={`/${eventType?.slug}s/${category?.slug}/`}
        className='category'
        key={`render-cats-${eventType?.slug}s/${category?.slug}`}
        tabIndex={-1}
      >
        {category.title}
      </CategorySeparator>
    )
  })
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({ items }) => {
  const [slideIndex, setSlideIndex] = useState(0)
  const numOfItems = items.length
  const nextIndex = slideIndex + 1 >= numOfItems ? 0 : slideIndex + 1
  const slider = createRef<Slider>()

  const settings = {
    arrows: false,
    dots: false,
    fade: true,
    draggable: false,
    pauseOnHover: true,
    lazyLoad: 'progressive',
    autoplay: true,
    autoplaySpeed: 6000,
    speed: 1000,
    beforeChange: (current, next): void => setSlideIndex(next),
  }

  const goToNextSlide = (): void => {
    if (slider.current) {
      slider.current.slickNext()
    }
  }

  const goToPreviousSlide = (): void => {
    if (slider.current) {
      slider.current.slickPrev()
    }
  }

  // render
  const renderNext = (): string => {
    return items[nextIndex].node.preTitle
      ? `${items[nextIndex].node.preTitle} ${items[nextIndex].node.title}`
      : items[nextIndex].node.title || ''
  }

  return (
    <HeroCarouselContainer>
      <StyledSlider {...settings} ref={slider}>
        {items
          // Skip items with invalid fields
          .filter(({ node }: HeroCarouselNode) => node.eventType && node.slug)
          .map(({ node }: HeroCarouselNode, index) => {
            const {
              preTitle,
              title,
              slug,
              startDate,
              endDate,
              customDateText,
              categories,
              eventType,
              imageWithFocalPoint,
              heroImage,
              viewMoreButtonText,
            } = node

            const formattedDateTime =
              startDate && endDate && formatDateTime(startDate, endDate)

            if (imageWithFocalPoint?.image)
              return (
                <HeroContainer key={slug}>
                  <Parallax
                    image={
                      getImage(imageWithFocalPoint?.image) ||
                      getImage(heroImage)
                    }
                    visibleOverflow={true}
                    focalRatio={
                      imageWithFocalPoint && GetFocalRatio(imageWithFocalPoint)
                    }
                  >
                    <HeroDetails>
                      <HeroCategory>
                        <CategorySeparator
                          to={`${eventType.slug}s/`}
                          tabIndex={-1}
                        >
                          {eventType.title}
                        </CategorySeparator>
                        {categories && renderCategories(eventType, categories)}
                        {isCurrentlyOn(startDate, endDate) && (
                          <CategorySeparator as='span' tabIndex={-1}>
                            Currently on
                          </CategorySeparator>
                        )}
                      </HeroCategory>
                      <HeroTitle>
                        {preTitle && (
                          <div>
                            <AnimateText
                              active={slideIndex === index}
                              text={preTitle}
                            />
                          </div>
                        )}
                        <AnimateText
                          active={slideIndex === index}
                          text={title}
                        />
                      </HeroTitle>
                      <HeroDate>
                        {customDateText ? customDateText : formattedDateTime}
                      </HeroDate>
                      <Button
                        to={`/${eventType.slug}s/${slug}/`}
                        className='view-more'
                        tabIndex={-1}
                      >
                        {viewMoreButtonText || 'View more'}
                      </Button>
                    </HeroDetails>
                  </Parallax>
                </HeroContainer>
              )
          })}
      </StyledSlider>
      <HeroControlContainer data-test-id='hero-carousel'>
        <HeroControlIndicator numOfItems={numOfItems} index={slideIndex} />
        <HeroControls>
          <ExtendedH3>Next: {renderNext()}</ExtendedH3>
          <HeroControlButtons>
            <NavigateBefore onClick={(): void => goToPreviousSlide()} />
            <NavigateNext onClick={(): void => goToNextSlide()} />
          </HeroControlButtons>
        </HeroControls>
      </HeroControlContainer>
      <BackgroundFill>
        <MetaInformationContainer>
          <MetaInformation>
            <MetaSection>
              <StyledArrowLink to='/visit/opening-hours/'>
                Opening hours
              </StyledArrowLink>
            </MetaSection>
            <MetaSection>
              <StyledArrowLink to='/visit/location-and-parking/'>
                Location and parking
              </StyledArrowLink>
            </MetaSection>
            <MetaSection>
              <StyledArrowLink to='/visit/prices'>
                Ticket prices
              </StyledArrowLink>
            </MetaSection>
          </MetaInformation>
        </MetaInformationContainer>
      </BackgroundFill>
    </HeroCarouselContainer>
  )
}

export default HeroCarousel
